"use client";

import * as React from "react";
import { useState, useEffect } from "react";
import { Spinner } from "./loading-spinner";
import { useTranslation } from "../hooks/useTranslation";

type RankSnapshot = {
  rank: number | string | null | undefined;
  score: number | string | null | undefined;
};

interface PointsCardProps {
  className?: string;
  isLoading?: boolean;
  weekRank?: RankSnapshot | null;
  overallRank?: RankSnapshot | null;
}

interface StatRowProps {
  label: string;
  value: number | string | null | undefined;
  isLoading?: boolean;
  highlight?: boolean;
  trend?: "up" | "down";
  formatOptions?: Intl.NumberFormatOptions;
}

function toNumber(value: number | string | null | undefined) {
  if (value === null || value === undefined) return null;
  const parsed = typeof value === "string" ? Number(value) : value;
  return Number.isNaN(parsed) ? null : parsed;
}

function formatNumber(
  value: number | string | null | undefined,
  options?: Intl.NumberFormatOptions
) {
  const numericValue = toNumber(value);
  if (numericValue === null) {
    return "–";
  }

  try {
    return new Intl.NumberFormat(undefined, {
      maximumFractionDigits: 0,
      ...options,
    }).format(numericValue);
  } catch {
    return numericValue.toString();
  }
}

const TrendIcon = ({ direction }: { direction: "up" | "down" }) => {
  if (direction === "down") {
    return (
      <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M6 3L1 8h10z" fill="#bb0031" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M6 9L1 4h10z" fill="#26bc4d" transform="rotate(180 6 6)" />
    </svg>
  );
};

const StatRow = ({
  label,
  value,
  isLoading,
  highlight,
  trend,
  formatOptions,
}: StatRowProps) => {
  const displayValue = formatNumber(value, formatOptions);
  const showTrend = !isLoading && value !== null && value !== undefined && trend;

  return (
    <div
      className={`flex gap-6 h-8 items-center px-6 py-0 ${
        highlight ? "bg-[#f5f5f5]" : ""
      }`}
    >
      <div className="flex-1 text-sm font-normal text-gray-900">{label}</div>
      <div className="flex gap-1 items-center justify-end w-[68px]">
        {isLoading ? (
          <Spinner size={14} className="text-gray-400" />
        ) : (
          <p className="text-sm font-bold text-gray-900 text-center whitespace-nowrap">
            {displayValue}
          </p>
        )}
        {showTrend ? (
          <div className="w-3 h-3">
            <TrendIcon direction={trend!} />
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default function PointsCard({
  className = "",
  isLoading = false,
  weekRank = null,
  overallRank = null,
}: PointsCardProps) {
  const { t } = useTranslation();
  const weeklyPoints = toNumber(weekRank?.score);
  const generalPoints = toNumber(overallRank?.score);
  const headlineScore = weeklyPoints ?? generalPoints;
  
  const [isAlertClosed, setIsAlertClosed] = useState(false);
  const ALERT_STORAGE_KEY = 'betcenter_overall_rank_alert_closed';

  useEffect(() => {
    // Check if alert was previously closed
    const stored = localStorage.getItem(ALERT_STORAGE_KEY);
    if (stored === 'true') {
      setIsAlertClosed(true);
    }
  }, []);

  const handleCloseAlert = () => {
    setIsAlertClosed(true);
    localStorage.setItem(ALERT_STORAGE_KEY, 'true');
  };

  return (
    <div
      className={`bg-white rounded-[16px] ${className}`}
      data-name="Points card"
      style={{ boxShadow: "0px 2px 8px 0px rgba(0,0,0,0.08)" }}
    >
      {/* Alert for overall ranking */}
      {!isAlertClosed ? (
          <div className="mx-6 my-3 p-3 bg-gray-100 rounded-lg border-l-4 border-gray-400 shadow-sm relative">
            <div className="flex items-start gap-2">
              <svg
                className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
              <p className="text-sm font-medium text-gray-800 leading-relaxed flex-1">
                {t('pointsCard.overallRankAlert')}
              </p>
              <button
                onClick={handleCloseAlert}
                className="flex-shrink-0 text-gray-500 hover:text-gray-700 transition-colors"
                aria-label="Close alert"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>
        ) : (      <div className="flex gap-8 items-center justify-center p-6">
          <div className="bg-[#26bc4d] rounded-[16px] px-4 py-2.5 w-20 h-16 flex items-center justify-center">
            <div className="text-center">
              {isLoading ? (
                <Spinner size={20} className="text-white mx-auto" />
              ) : (
                <p className="font-bold text-2xl leading-tight text-white">
                  {formatNumber(headlineScore, { maximumFractionDigits: 2 })}
                </p>
              )}
              <p className="text-xs font-normal leading-tight text-white">
                {t('pointsCard.yourScore')}
              </p>
            </div>
          </div>
        </div>)}

      <div className="space-y-0">
        <StatRow
          label={t('pointsCard.weekRank')}
          value={weekRank?.rank ?? null}
          isLoading={isLoading}
          trend="up"
        />
        <StatRow
          label={t('pointsCard.overallRank')}
          value={overallRank?.rank ?? null}
          isLoading={isLoading}
          highlight
          trend="down"
        />
        <StatRow
          label={t('pointsCard.overallPoints')}
          value={overallRank?.score ?? null}
          isLoading={isLoading}
          trend="up"
          formatOptions={{ maximumFractionDigits: 2 }}
        />
      </div>
    </div>
  );
}
